/**
 * OrderListPage.js - 订单列表管理页
 * 支持按状态 Tab 切换、关键词搜索，点击查看订单详情
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tabs,
  Input,
  Button,
  Tag,
  Space,
  Typography,
  message,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  DatePicker,
  Modal,
} from 'antd';
import { SearchOutlined, EyeOutlined, DeleteOutlined, DownloadOutlined, FileExcelOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { adminOrderApi, adminPackingSlipApi } from '../../api/index';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// 订单状态颜色映射
const statusColorMap = {
  pending: 'orange',
  paid: 'green',
  shipped: 'blue',
  completed: 'purple',
  cancelled: 'red',
};

// 订单状态中文标签
const statusLabelMap = {
  pending: '待支付',
  paid: '已支付',
  shipped: '已发货',
  completed: '已完成',
  cancelled: '已取消',
};

// 财务状态中文标签
const financialStatusLabelMap = {
  pending: '待支付',
  paid: '已支付',
  refunded: '已退款',
  partial_refunded: '部分退款',
  voided: '已作废',
};

// Tab 配置
const TAB_ITEMS = [
  { key: 'all', label: '全部', status: undefined, financialStatus: undefined, isDraft: undefined },
  { key: 'pending_shipment', label: '待发货', status: 'paid', financialStatus: undefined, isDraft: false, filterFn: (o) => o.financialStatus === 'paid' || o.financialStatus === 'partially_refunded' },
  { key: 'completed', label: '已完成', status: undefined, financialStatus: undefined, isDraft: false, filterFn: (o) => o.status === 'shipped' || o.status === 'completed' },
  { key: 'cancelled', label: '已取消', status: 'cancelled', financialStatus: undefined, isDraft: undefined },
];

export default function OrderListPage() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // 当前激活 Tab
  const [activeTab, setActiveTab] = useState('all');
  // 搜索关键词（订单号或邮箱）
  const [keyword, setKeyword] = useState('');
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  // 选中的订单 ID 列表
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  // 订单创建时间筛选范围
  const [searchDateRange, setSearchDateRange] = useState(null);
  // 是否正在导出 PDF
  const [exporting, setExporting] = useState(false);
  // 导出报告弹窗
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportDateRange, setExportDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [exportReportLoading, setExportReportLoading] = useState(false);

  // 获取当前 Tab 对应的筛选参数
  const getCurrentTabConfig = useCallback(() => {
    return TAB_ITEMS.find((t) => t.key === activeTab) || TAB_ITEMS[0];
  }, [activeTab]);

  // 拉取订单列表
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const tabConfig = getCurrentTabConfig();
    try {
      const data = await adminOrderApi.list({
        keyword: keyword || undefined,
        status: tabConfig.status,
        financialStatus: tabConfig.financialStatus,
        isDraft: tabConfig.isDraft,
        startDate: searchDateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: searchDateRange?.[1]?.format('YYYY-MM-DD'),
        page,
        pageSize,
      });
      let ordersList = [];
      let totalCount = 0;
      if (Array.isArray(data)) {
        ordersList = data;
        totalCount = data.length;
      } else {
        ordersList = data?.list || data?.items || [];
        totalCount = data?.total || 0;
      }
      // 应用客户端筛选（针对复合条件）
      if (tabConfig.filterFn) {
        ordersList = ordersList.filter(tabConfig.filterFn);
        totalCount = ordersList.length;
      }
      setOrders(ordersList);
      setTotal(totalCount);
      setSelectedOrderIds([]);
    } catch (err) {
      message.error('加载订单列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [keyword, page, pageSize, searchDateRange, getCurrentTabConfig]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Tab 切换时重置分页
  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
  };

  // 搜索
  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  // 删除订单
  const handleDelete = async (id) => {
    try {
      await adminOrderApi.delete(id);
      message.success('订单已删除');
      fetchOrders();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 批量删除订单
  const handleBatchDelete = () => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择要删除的订单');
      return;
    }

    Modal.confirm({
      title: '确认批量删除订单？',
      icon: <ExclamationCircleOutlined />,
      content: `将删除 ${selectedOrderIds.length} 个已发货/已完成订单，并同步清理订单商品、支付记录和退款记录。删除后无法恢复。`,
      okText: '批量删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await adminOrderApi.batchDelete(selectedOrderIds);
          message.success('订单已批量删除');
          setSelectedOrderIds([]);
          fetchOrders();
        } catch (err) {
          message.error('批量删除失败：' + (err?.response?.data?.message || err.message || '未知错误'));
        }
      },
    });
  };

  // 导出装箱单 PDF
  const handleExportPackingSlip = async () => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择至少一个订单');
      return;
    }
    setExporting(true);
    try {
      const blob = await adminPackingSlipApi.print(selectedOrderIds);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `装箱单_${dateStr}_${selectedOrderIds.length}单.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('装箱单已导出');
    } catch (err) {
      message.error('导出失败：' + (err.message || '未知错误'));
    } finally {
      setExporting(false);
    }
  };

  // 导出订单报告
  const handleExportReport = async () => {
    if (!exportDateRange || exportDateRange.length !== 2) {
      message.warning('请选择时间范围');
      return;
    }
    const startDate = exportDateRange[0].format('YYYY-MM-DD');
    const endDate = exportDateRange[1].format('YYYY-MM-DD');
    setExportReportLoading(true);
    try {
      const blob = await adminOrderApi.exportReport(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `订单报告_${startDate}_${endDate}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('订单报告已导出');
      setExportModalVisible(false);
    } catch (err) {
      message.error('导出失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    } finally {
      setExportReportLoading(false);
    }
  };

  // 表格行选择变化
  const rowSelection = {
    selectedRowKeys: selectedOrderIds,
    onChange: (selectedKeys) => {
      setSelectedOrderIds(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'shipped' && record.status !== 'completed',
      name: record.orderNo,
    }),
  };

  // 表格列配置
  const columns = [
    {
      title: '订单号 / 客户',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (text, record) => {
        const email = record.customerEmail || record.email || record.customer?.email || '';
        return (
          <div>
            <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>#{text}</span>
            {email && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{email}</div>}
          </div>
        );
      },
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={statusColorMap[s] || 'default'}>
          {statusLabelMap[s] || s || '-'}
        </Tag>
      ),
    },
    {
      title: '财务状态',
      dataIndex: 'financialStatus',
      key: 'financialStatus',
      render: (s) => (
        <span style={{ fontSize: 13, color: '#666' }}>
          {financialStatusLabelMap[s] || s || '-'}
        </span>
      ),
    },
    {
      title: '金额',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (val) => `$${Number(val || 0).toFixed(2)}`,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) =>
        val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/orders/${record.id}`);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除该订单？"
            description="删除后无法恢复，请谨慎操作。"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16, marginTop: 0 }}>
        订单管理
      </Title>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        {/* 搜索栏 + 导出按钮 */}
        <div style={{ padding: '16px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Row gutter={[12, 12]} style={{ flex: 1 }}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="搜索订单号、邮箱、手机号、支付参考号、物流单号"
                prefix={<SearchOutlined />}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                value={searchDateRange}
                onChange={(dates) => {
                  setSearchDateRange(dates);
                  setPage(1);
                }}
                style={{ width: '100%' }}
                placeholder={['开始时间', '结束时间']}
                presets={[
                  { label: '今天', value: [dayjs(), dayjs()] },
                  { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
                  { label: '最近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
                  { label: '本月', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                ]}
              />
            </Col>
            <Col xs={24} sm={4} md={3}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                搜索
              </Button>
            </Col>
          </Row>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportPackingSlip}
            loading={exporting}
            disabled={selectedOrderIds.length === 0}
          >
            导出装箱单 {selectedOrderIds.length > 0 && `(${selectedOrderIds.length})`}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleBatchDelete}
            disabled={selectedOrderIds.length === 0}
          >
            批量删除 {selectedOrderIds.length > 0 && `(${selectedOrderIds.length})`}
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={() => setExportModalVisible(true)}
          >
            导出报告
          </Button>
        </div>

        {/* 状态 Tab 切换 */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          style={{ padding: '0 16px' }}
          items={TAB_ITEMS.map((tab) => ({
            key: tab.key,
            label: tab.label,
          }))}
        />

        {/* 订单表格 */}
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          style={{ padding: '0' }}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/orders/${record.id}`),
            style: {
              cursor: 'pointer',
              ...(record.status === 'shipped' ? { backgroundColor: '#4a4a4a', color: '#ccc' } : {}),
            },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
            style: { padding: '0 16px 16px' },
          }}
          locale={{ emptyText: '暂无订单数据' }}
        />
      </Card>

      {/* 导出报告弹窗 */}
      <Modal
        title="导出订单报告"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onOk={handleExportReport}
        okText="导出"
        cancelText="取消"
        confirmLoading={exportReportLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}>选择要导出的订单时间范围：</p>
          <RangePicker
            value={exportDateRange}
            onChange={(dates) => setExportDateRange(dates)}
            style={{ width: '100%' }}
            presets={[
              { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
              { label: '最近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
              { label: '本月', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
              { label: '上月', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
            ]}
          />
          <p style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
            导出的 CSV 文件将包含订单基本信息及每个订单的所有商品 SKU 详情。
          </p>
        </div>
      </Modal>
    </div>
  );
}
